import axios from 'axios';
import useAuthStore from '../store/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Utility function for authenticated file downloads
export const downloadFile = async (fileUrl, fileName) => {
  try {
    // Extract the path after /api/files/
    let filePath = fileUrl;
    if (fileUrl.startsWith('/api/files/')) {
      filePath = fileUrl.substring('/api/files/'.length);
    } else if (fileUrl.includes('/api/files/')) {
      filePath = fileUrl.split('/api/files/')[1];
    }
    
    // Get the original filename for download name
    const urlParts = filePath.split('/');
    const originalFilename = urlParts[urlParts.length - 1];
    const downloadName = fileName || originalFilename;
    
    console.log(`[Download] Starting download: ${filePath}`);
    
    // Use authenticated download with blob - pass the full path
    const response = await api.get(`/files/${filePath}`, {
      responseType: 'blob',
      timeout: 60000, // 60 second timeout for large files
    });
    
    // Check if we got an error response as JSON
    if (response.data.type === 'application/json') {
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      console.error('[Download] Server error:', errorData);
      throw new Error(errorData.detail || 'Download failed');
    }
    
    console.log(`[Download] Received ${response.data.size} bytes`);
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
    const blobUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
    
    console.log(`[Download] Success: ${downloadName}`);
    return { success: true, fileName: downloadName };
  } catch (error) {
    console.error('[Download] Error:', error.response?.status, error.message);
    
    // Provide specific error messages
    if (error.response?.status === 404) {
      throw new Error('File not found. It may have been deleted or uploaded in a different environment.');
    } else if (error.response?.status === 401) {
      throw new Error('Session expired. Please log in again.');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Download timed out. Please try again.');
    }
    
    throw error;
  }
};

// Get authenticated blob URL for viewing media
export const getMediaBlobUrl = async (fileUrl) => {
  try {
    // Extract the path after /api/files/
    let filePath = fileUrl;
    if (fileUrl.startsWith('/api/files/')) {
      filePath = fileUrl.substring('/api/files/'.length);
    } else if (fileUrl.includes('/api/files/')) {
      filePath = fileUrl.split('/api/files/')[1];
    }
    
    console.log(`[Media] Loading: ${filePath}`);
    
    const response = await api.get(`/files/${filePath}`, {
      responseType: 'blob'
    });
    
    return window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
  } catch (error) {
    console.error('Failed to load media:', error);
    return null;
  }
};

export default api;